import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Marker } from "react-map-gl/mapbox";
import { logger } from "../../../utils/logger";
import { getDeviceFreshnessTime } from "../../../utils/deviceUtils";
import { getMapboxMap } from "../../../utils/mapHelpers";

/**
 * Hook for virtualizing device markers based on map bounds
 * Only renders markers that are within the current viewport
 */
const useVirtualizedMarkers = (
  devices,
  mapRef,
  mapLoaded,
  maxMarkers = 1000
) => {
  const [mapBounds, setMapBounds] = useState(null);

  // Update bounds when map moves
  useEffect(() => {
    const map = getMapboxMap(mapRef);
    if (!mapLoaded || !map) return;

    const updateBounds = () => {
      try {
        const bounds = map.getBounds();
        if (bounds) {
          setMapBounds({
            west: bounds.getWest(),
            east: bounds.getEast(),
            south: bounds.getSouth(),
            north: bounds.getNorth(),
          });
        }
      } catch (error) {
        logger.error("Error getting map bounds:", error);
      }
    };

    // Initial bounds
    updateBounds();

    // Listen for map move events
    map.on("moveend", updateBounds);
    map.on("zoomend", updateBounds);

    return () => {
      map.off("moveend", updateBounds);
      map.off("zoomend", updateBounds);
      setMapBounds(null);
    };
  }, [mapLoaded, mapRef]);

  const visibleDevices = useMemo(() => {
    if (!devices.length) return devices;

    // If device count is under threshold, render all
    if (devices.length <= maxMarkers) return devices;

    // If no bounds yet, return limited devices
    if (!mapBounds) return devices.slice(0, maxMarkers);

    try {
      // Filter devices within viewport bounds
      const visibleInBounds = devices.filter((device) => {
        if (!device.lat || !device.lng) return false;

        const lng = parseFloat(device.lng);
        const lat = parseFloat(device.lat);

        if (isNaN(lng) || isNaN(lat)) return false;

        return (
          lng >= mapBounds.west &&
          lng <= mapBounds.east &&
          lat >= mapBounds.south &&
          lat <= mapBounds.north
        );
      });

      // If still too many visible markers, prioritize by recency or importance
      if (visibleInBounds.length > maxMarkers) {
        return visibleInBounds
          .sort(
            (a, b) =>
              Date.parse(getDeviceFreshnessTime(b) || 0) -
              Date.parse(getDeviceFreshnessTime(a) || 0)
          )
          .slice(0, maxMarkers);
      }

      return visibleInBounds;
    } catch (error) {
      logger.error("Error filtering visible devices:", error);
      return devices.slice(0, maxMarkers);
    }
  }, [devices, mapBounds, maxMarkers]);

  return visibleDevices;
};

/**
 * Optimized device marker component with React.memo
 */
const DeviceMarker = React.memo(
  ({ device, isOutdated, onClick, onMouseEnter, onMouseLeave }) => {
    const handleClick = useCallback(
      (e) => {
        e.originalEvent.stopPropagation();
        onClick?.(device);
      },
      [device, onClick]
    );

    const handleMouseEnter = useCallback(() => {
      onMouseEnter?.(device);
    }, [device, onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
      onMouseLeave?.(device);
    }, [device, onMouseLeave]);

    // Skip rendering if coordinates are invalid
    if (!device.lat || !device.lng || device.lat === 0 || device.lng === 0) {
      return null;
    }

    return (
      <Marker
        longitude={parseFloat(device.lng)}
        latitude={parseFloat(device.lat)}
        color={
          isOutdated(getDeviceFreshnessTime(device)) ? "#f44336" : "#4caf50"
        }
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "pointer" }}
      />
    );
  }
);

DeviceMarker.displayName = "DeviceMarker";

/**
 * Virtualized device markers list component
 */
export const VirtualizedDeviceMarkers = React.memo(
  ({
    devices,
    mapRef,
    mapLoaded,
    isDeviceOutdated,
    onDeviceClick,
    onDeviceHover,
    onDeviceLeave,
    maxMarkers = 1000,
  }) => {
    const visibleDevices = useVirtualizedMarkers(
      devices,
      mapRef,
      mapLoaded,
      maxMarkers
    );

    // Batch render markers in chunks to prevent blocking
    const markerChunks = useMemo(() => {
      const chunkSize = 100;
      const chunks = [];
      for (let i = 0; i < visibleDevices.length; i += chunkSize) {
        chunks.push(visibleDevices.slice(i, i + chunkSize));
      }
      return chunks;
    }, [visibleDevices]);

    return (
      <>
        {markerChunks.map((chunk, chunkIndex) => (
          <React.Fragment key={`chunk-${chunkIndex}`}>
            {chunk.map((device) => (
              <DeviceMarker
                key={device.id}
                device={device}
                isOutdated={isDeviceOutdated}
                onClick={onDeviceClick}
                onMouseEnter={onDeviceHover}
                onMouseLeave={onDeviceLeave}
              />
            ))}
          </React.Fragment>
        ))}
      </>
    );
  }
);

VirtualizedDeviceMarkers.displayName = "VirtualizedDeviceMarkers";
